import * as React from "react";
import { cn } from "@/components/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScanEye, Eye, EyeOff } from "lucide-react";
import { TypographyH1 } from "../basicElement/Typography/TypographyH1";
import { TypographyH4 } from "../basicElement/Typography/TypographyH4";
import { Tlogin } from "@/api/AuthTypes";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom"; // Importez Link
import { toast } from "react-toastify";

interface UserAuthFormProps extends React.HTMLAttributes<HTMLDivElement> {}

type RedirectMap = {
  [key: string]: string;
};

export function UserAuthForm({ className, ...props }: UserAuthFormProps) {
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [showPassword, setShowPassword] = React.useState<boolean>(false);
  const { signin, userContext } = useAuth();
  const navigate = useNavigate();

  // Redirection en fonction du rôle de l'utilisateur
  const redirectMap: RedirectMap = {
    ADMIN: `/dashboard/licences`,
    PRATICIEN: `/praticien/patients`,
    PATIENT: `/patient/seances`,
  };

  React.useEffect(() => {
  if (userContext?.role) {
    const redirectUrl = redirectMap[userContext.role];
    navigate(redirectUrl);
  }
  }, [userContext?.role]);

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  async function onSubmit(event: React.SyntheticEvent) {
    event.preventDefault();
    setIsLoading(true);
    setErrorMessage(null); // Réinitialiser le message d'erreur

    try {
      const data: Tlogin = { email, password };
      const response = await signin(data);

      const redirectUrl = redirectMap[response.data.role];

      if (!redirectUrl) {
        throw new Error("Rôle utilisateur non reconnu");
      }

      navigate(redirectUrl);
    } catch (error: unknown) {
      if (error instanceof Error) {
        setErrorMessage(error.message);
        toast.error(
          error.message || "Une erreur est survenue. Veuillez réessayer."
        );
      } else {
        toast.error("Une erreur est survenue. Veuillez réessayer.");
      }
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className={cn("grid gap-6", className)} {...props}>
      <form onSubmit={onSubmit} className="h-fullscreen">
        <div className="grid gap-2 mb-12">
          <div className="mb-12">
            <TypographyH1>IRON</TypographyH1>
            <TypographyH4>
              Interface de Rééducation Optocinétique et
              Neurovisuelle
            </TypographyH4>
          </div>
          <div className="grid gap-1">
            <Label className="sr-only" htmlFor="email">
              Email
            </Label>
            <Input
              id="email"
              placeholder="nom@exemple.fr"
              type="email"
              autoCapitalize="none"
              autoComplete="email"
              autoCorrect="off"
              disabled={isLoading}
              value={email}
              onChange={handleEmailChange}
            />
          </div>
          <div className="relative grid gap-1">
  <Label className="sr-only" htmlFor="password">
    Mot de passe
  </Label>
  <Input
    id="password"
    placeholder="Entrez votre mot de passe"
    type={showPassword ? "text" : "password"}
    autoCapitalize="none"
    autoComplete="current-password"
    autoCorrect="off"
    disabled={isLoading}
    value={password}
    onChange={handlePasswordChange}
    className="pr-10" // Ajoutez un padding à droite pour accueillir l'icône
  />
  <button
    type="button"
    onClick={togglePasswordVisibility}
    className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500"
    aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
  >
    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
  </button>
</div>

          {/* Affichage du message d'erreur */}
          {errorMessage && <div className="text-red-500">{errorMessage}</div>}

          <Button disabled={isLoading}>
            {isLoading && <ScanEye className="mr-2 h-4 w-4 animate-spin" />}
            Se connecter
          </Button>

          {/* Lien pour réinitialiser le mot de passe */}
          <div className="mt-4 text-center">
            <Link to="/mot-de-passe-oublie" className="text-blue-500">
              Mot de passe oublié ou première connexion ?
            </Link>
          </div>
        </div>
      </form>
    </div>
  );
}
